import React from 'react'
import { TabPanel } from 'primereact/tabview'

import { ChargepointLatestChargingSessionsWidget } from '../../../common/components/charge-point-latest-charging-sessions-widget'
import useRemoteControlAutoRefresh from '../../../common/hooks/useRemoteControlAutoRefresh'
import { CapiCurrentSettings } from '../../../lib/capi-current-settings'
import { ChargePointCapiAuraControlWidget } from '../../../lib/charge-point-capi-aura-control-widget'
import { ChargePointCapiHaloControlWidget } from '../../../lib/charge-point-capi-halo-control-widget'
import {
    isAdminDomain,
    isLandingPage,
    isPartnerDomain,
    isSupportDomain,
    isUserappDomain,
} from '../../../lib/currentDomain'
import isAura from '../../../lib/isAura'
import isHalo from '../../../lib/isHalo'
import { ChargePointMaintenanceLogsWidget } from '../../../common/components/charge-point-maintenance-logs-widget'
import { ChargePointLogsWidget } from '../../components/charge-point-logs-widget'
import { ChargepointChargerDataWidget } from '../../components/chargepoint-charger-data-widget'
import { ChargePointCapiDetailsWidget } from '../../features/charge-point-capi-details-widget'
import { ChargePointPropertiesWidget } from '../../features/charge-point-properties-widget'
import { ChargePointCapiScheduledChargingWidget } from '../../../lib/charge-point-capi-scheduled-charging-widget'
import { ChargePointPartnerInfoWidget } from '../../../common/features/charge-point-partner-info-widget/charge-point-partner-info-widget'
import { ChargePoint } from '../../../common/redux/api/web-api'
import useText from '../../../common/hooks/useText'
import { LazyTabView } from '../../../common/uikit/LazyTabView'
import { ChargePointAlarmsWidget } from '../../../common/components/charge-point-alarms-widget'
import { ChargepointIntegrationsWidget } from '../../../common/features/charge-point-integrations-widget'

export interface ChargePointCapiPageProps {
    chargePoint: ChargePoint
    isLoading: boolean
}

export function ChargePointCapiPage({ chargePoint, isLoading }: ChargePointCapiPageProps): React.ReactElement | null {
    const text = useText()

    const skipRemoteControlAutoRefresh = isUserappDomain && isLandingPage(window.location.pathname)
    const remoteControlData = useRemoteControlAutoRefresh(chargePoint.id, skipRemoteControlAutoRefresh)

    return (
        <LazyTabView>
            <TabPanel header={text('lblPropertiesWidget')} leftIcon="pi pi-list mr-2">
                <div className="grid">
                    <div className="lg:col-6 col-12">
                        <ChargePointPropertiesWidget chargePoint={chargePoint} disabled={isLoading} />
                    </div>
                    <div className="lg:col-6 col-12">
                        {chargePoint && isHalo(chargePoint) && (
                            <ChargePointCapiHaloControlWidget chargePoint={remoteControlData ?? chargePoint} />
                        )}
                        {chargePoint && isAura(chargePoint) && (
                            <ChargePointCapiAuraControlWidget chargePoint={remoteControlData ?? chargePoint} />
                        )}
                    </div>
                </div>
            </TabPanel>
            <TabPanel header={text('lblChargerSettings')} leftIcon="pi pi-cog mr-2">
                <ChargePointCapiDetailsWidget chargePoint={chargePoint} loading={isLoading} />
            </TabPanel>
            {(isPartnerDomain || isSupportDomain) && (
                <TabPanel header={text('btnIntegrations')} leftIcon="pi pi-link mr-2">
                    <ChargepointIntegrationsWidget chargePoint={chargePoint} />
                </TabPanel>
            )}
            <TabPanel header={text('lblElectricalSettings')} leftIcon="pi pi-bolt mr-2">
                <CapiCurrentSettings chargePoint={chargePoint} isRestrictedAccess={false} />
            </TabPanel>
            {(isPartnerDomain || isSupportDomain) && (
                <TabPanel header={text('lblLogs')} leftIcon="pi pi-receipt mr-2">
                    <ChargePointLogsWidget chargePointId={chargePoint.id} />
                </TabPanel>
            )}

            <TabPanel header={text('lblSchedules')} leftIcon="pi pi-calendar mr-2">
                <ChargePointCapiScheduledChargingWidget chargePoint={chargePoint} />
            </TabPanel>

            {!isAdminDomain && (
                <TabPanel header={text('lblAlerts')} leftIcon="pi pi-bell mr-2">
                    {isPartnerDomain && <ChargePointAlarmsWidget />}

                    <ChargePointMaintenanceLogsWidget />
                </TabPanel>
            )}
            {!isAdminDomain && (
                <TabPanel header={text('lblChargingSessionsWidget')} leftIcon="pi pi-wave-pulse mr-2">
                    <ChargepointLatestChargingSessionsWidget
                        chargePointId={chargePoint.id}
                        deleteEnabled={isSupportDomain}
                        useFilter={isSupportDomain || isPartnerDomain}
                    />
                </TabPanel>
            )}

            {isSupportDomain && (
                <TabPanel header={text('lblChargerData')} leftIcon="pi pi-objects-column mr-2">
                    <ChargepointChargerDataWidget chargePointId={chargePoint.id} />
                </TabPanel>
            )}
            {isAdminDomain && (
                <TabPanel header={text('lbllinkSupport')} leftIcon="pi pi-phone mr-2">
                    <ChargePointPartnerInfoWidget chargePointId={chargePoint.id} />
                </TabPanel>
            )}
        </LazyTabView>
    )
}
